import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { XCircle, AlertTriangle } from 'lucide-react';
import AuthLayout from '@/components/auth-layout';
import AuthCard from '@/components/auth-card';
import useErrorDetails from '@/hooks/use-error-details';
import { containerVariants, itemVariants } from '@/lib/animations';

const ErrorPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const errorId: string | null = new URLSearchParams(location.search).get('errorId');
  const { error } = useErrorDetails(errorId);

  return (
    <AuthLayout>
      <AuthCard>
        <motion.div
          className="flex flex-col items-center gap-4 sm:gap-6 py-4 sm:py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex justify-center">
            <motion.div
              className="p-3 sm:p-4 rounded-full bg-destructive/10"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            >
              <AlertTriangle className="h-8 w-8 sm:h-10 sm:w-10 text-destructive" />
            </motion.div>
          </motion.div>

          <motion.h2
            variants={itemVariants}
            className="text-lg sm:text-xl md:text-2xl font-bold text-center"
          >
            {t('errors.genericError')}
          </motion.h2>

          <motion.div
            variants={itemVariants}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
          >
            <XCircle className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 text-destructive" />
          </motion.div>

          {error && (
            <>
              <motion.h3
                variants={itemVariants}
                className="text-base sm:text-lg font-semibold text-center"
              >
                {error.error}
              </motion.h3>
              {error.description && (
                <motion.p
                  variants={itemVariants}
                  className="text-sm sm:text-base text-muted-foreground text-center max-w-md"
                >
                  {error.description}
                </motion.p>
              )}
            </>
          )}
        </motion.div>
      </AuthCard>
    </AuthLayout>
  );
}

export default ErrorPage;